import {
  AgentDetails,
  CommunicationPreferences as CommunicationPreferencesModel,
} from '@urbanx/agx.agencies.client';
import { TeamMember } from '@urbanx/agx.agencies.client';
import {
  AgxAvatarLabelGroup,
  AgxCheckbox,
  AgxSpacer,
  AgxTable,
  IColumnConfig,
} from '@urbanx/agx.ui-components2';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import { useCallback, useMemo } from 'react';

interface CommunicationPreferencesProps {
  members: TeamMember[];
  agents: AgentDetails[];
  onPreferencesChange: (members: TeamMember[]) => void;
}

const preferences = [
  { key: 'agreement', name: 'Agreement' },
  { key: 'listing', name: 'Listing' },
  { key: 'contract', name: 'Contract' },
  { key: 'commission', name: 'Commission' },
] as const satisfies { key: keyof CommunicationPreferencesModel; name: string }[];

const preferencesCols: IColumnConfig[] = [
  { name: 'Team Member', width: '20%' },
  ...preferences.map(({ name }) => ({ name, align: 'center' as const, width: '20%' })),
];

interface TeamMemberWithDetails extends TeamMember {
  photoUri?: string;
  fullName?: string;
}

const CommunicationPreferences = ({
  members,
  agents,
  onPreferencesChange,
}: CommunicationPreferencesProps) => {
  const membersWithDetails: TeamMemberWithDetails[] = useMemo(() => {
    return members.map((member) => {
      const agent = agents?.find((agent) => agent.id === member.agentId);
      return {
        ...member,
        photoUri: agent?.photo?.publicPath,
        fullName: agent ? `${agent.firstName} ${agent.lastName}` : undefined,
      };
    });
  }, [members, agents]);

  const updateCommunicationPreference = useCallback(
    (targetMember: TeamMemberWithDetails, preference: CommunicationPreferencesModel) => {
      const updatedMembers = membersWithDetails.map((member) => {
        if (member.agentId === targetMember.agentId) {
          return {
            ...member,
            communicationPreferences: {
              ...member.communicationPreferences,
              ...preference,
            },
          };
        }
        return member;
      });

      onPreferencesChange(updatedMembers);
    },
    [membersWithDetails, onPreferencesChange]
  );

  const rows = useMemo(() => {
    return membersWithDetails.map((member, index) => ({
      ['Team Member']: (
        <AgxSpacer contentAlignment="start">
          <AgxAvatarLabelGroup
            id={`txtMemberName-${index}`}
            imageUrl={member.photoUri}
            label1={member.fullName ?? ''}
            mode="letters"
            backgroundMode="dark"
            size="sm"
          />
        </AgxSpacer>
      ),
      ...Object.fromEntries(
        preferences.map(({ key, name }) => [
          name,
          <AgxSpacer key={key} contentAlignment="center">
            <AgxCheckbox
              id={`chk${key}-${index}`}
              checked={member.communicationPreferences?.[key] ?? false}
              onChange={(checked) => updateCommunicationPreference(member, { [key]: checked })}
              style="no text"
            />
          </AgxSpacer>,
        ])
      ),
    }));
  }, [membersWithDetails, updateCommunicationPreference]);

  return (
    <AgxTable
      columns={preferencesCols}
      rows={rows}
      variant="minimal"
      tableStyle="bordered"
      headerBg={tokens.bg.tagLightGrey}
      responsive
      ariaLabel="Communication preferences"
    />
  );
};

export default CommunicationPreferences;
